if (!customElements.get('routine-finder')) {
  customElements.define('routine-finder', class RoutineFinder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-quiz-form]');
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.results = this.querySelector('[data-results]');
      this.emailForm = this.querySelector('.routine-finder__email form');
      this.catalog = JSON.parse(this.querySelector('[data-product-catalog]').textContent || '{}');
      this.metaobjectRoutines = JSON.parse(this.querySelector('[data-metaobject-routines]')?.textContent || '{}');
      this.currentStep = 0;
      this.activeLevel = 'simple';
      this.answers = {};
      this.rules = [];
      this.routines = {
        face: {
          label: 'Face care',
          title: 'Your face-care routine',
          text: 'A straightforward sequence for cleansing, applying your collagen product and completing the routine with daily moisturising care.',
          notice: 'Topical collagen primarily works at the skin surface, helping it retain moisture and feel smoother. This suggestion is cosmetic guidance, not a diagnosis.',
          simple: ['face_cleanser', 'face_collagen'],
          complete: ['face_cleanser', 'face_mist', 'face_collagen', 'day_cream', 'night_cream']
        },
        eyes: {
          label: 'Eyes and lips',
          title: 'Your eyes and lips routine',
          text: 'Focused cosmetic care for the delicate eye area, with an optional lip-care step.',
          notice: 'Avoid direct eye contact and follow each product’s directions. Seek professional advice for persistent irritation or a diagnosed eye or skin condition.',
          simple: ['eye_elixir', 'lip_gloss'],
          complete: ['face_cleanser', 'eye_serum', 'eye_elixir', 'lip_gloss']
        },
        body: {
          label: 'Body, hands and feet',
          title: 'Your body-care routine',
          text: 'Surface-conditioning care selected for body skin, with focused options for hands and feet.',
          notice: 'Do not apply cosmetic products to broken or infected skin. Check the complete ingredient list before use.',
          simple: ['body_collagen', 'hand_cream'],
          complete: ['body_collagen_large', 'hand_cream', 'foot_cream']
        },
        hair: {
          label: 'Hair and scalp',
          title: 'Your hair-care routine',
          text: 'A practical wash-and-condition routine, with optional scalp preparation and hair-and-nail gel in the complete version.',
          notice: 'The scalp peel is an occasional cosmetic step, not a treatment for dandruff, psoriasis or hair loss. Follow its directions and discontinue use if irritation occurs.',
          simple: ['shampoo', 'conditioner'],
          complete: ['scalp_peel', 'shampoo', 'conditioner', 'hair_nails']
        },
        men: {
          label: 'Men’s care',
          title: 'Your men’s daily-care routine',
          text: 'A concise routine for cleansing, surface hydration and after-shave comfort.',
          notice: 'This result offers cosmetic product guidance only. Check ingredients and directions before use.',
          simple: ['face_cleanser', 'after_shave'],
          complete: ['face_cleanser', 'face_collagen', 'after_shave']
        }
      };
      Object.entries(this.metaobjectRoutines).forEach(([key, override]) => {
        if (!this.routines[key]) return;
        this.routines[key] = {
          ...this.routines[key],
          ...Object.fromEntries(Object.entries(override).filter(([, value]) => value && (!Array.isArray(value) || value.length)))
        };
      });
      this.querySelectorAll('[data-routine-area]').forEach((script) => {
        const area = JSON.parse(script.textContent || '{}');
        if (!area.key) return;
        const registerProducts = (products = []) => products.map((product) => {
          this.catalog[product.key] = product;
          return product.key;
        });
        this.routines[area.key] = {
          label: area.label,
          title: area.title,
          text: area.text,
          notice: area.notice,
          collectionUrl: area.collectionUrl,
          simple: registerProducts(area.simpleProducts),
          complete: registerProducts(area.completeProducts)
        };
      });
      this.querySelectorAll('[data-routine-rule]').forEach((script) => {
        const rule = JSON.parse(script.textContent || '{}');
        const productKeys = (rule.products || []).map((product) => {
          this.catalog[product.key] = product;
          return product.key;
        });
        this.rules.push({ ...rule, productKeys });
      });

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.form?.addEventListener('submit', (event) => event.preventDefault());
      this.emailForm?.addEventListener('submit', this.submitEmail.bind(this));
      this.showStep(0);
    }

    onChange(event) {
      if (event.target.matches('[data-product-toggle]')) {
        this.updateSelection();
        return;
      }
      if (event.target.matches('input[type="checkbox"][name="area"]')) {
        this.answers.areas = [...this.querySelectorAll('input[name="area"]:checked')].map((input) => input.value);
        this.answers.area = this.answers.areas[0] || '';
        this.querySelector('[data-error]').textContent = '';
        return;
      }
      if (!event.target.matches('input[type="radio"]')) return;
      this.answers[event.target.name] = event.target.value;
      if (event.target.name === 'feel') this.answers.isSensitive = event.target.dataset.sensitive === 'true';
      this.querySelector('[data-error]').textContent = '';
    }

    onClick(event) {
      const next = event.target.closest('[data-next]');
      const back = event.target.closest('[data-back]');
      const restart = event.target.closest('[data-restart]');
      const tab = event.target.closest('[data-level]');
      const add = event.target.closest('[data-add-routine]');
      const editAnswers = event.target.closest('[data-edit-answers]');
      if (next) this.goNext();
      if (back) this.showStep(this.currentStep - 1);
      if (restart) this.restart();
      if (tab) this.setLevel(tab.dataset.level);
      if (add) this.addRoutine(add);
      if (editAnswers) {
        this.showStep(this.steps.length - 1);
        requestAnimationFrame(() => {
          const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        });
      }
    }

    goNext() {
      const step = this.steps[this.currentStep];
      const checked = step.querySelector('input:checked');
      if (!checked) {
        this.querySelector('[data-error]').textContent = 'Please choose one option to continue.';
        return;
      }
      if (this.currentStep < this.steps.length - 1) this.showStep(this.currentStep + 1);
      else this.showResults();
    }

    showStep(index) {
      this.currentStep = Math.max(0, Math.min(index, this.steps.length - 1));
      this.steps.forEach((step, i) => { step.hidden = i !== this.currentStep; });
      this.results.hidden = true;
      this.form.hidden = false;
      const percent = ((this.currentStep + 1) / this.steps.length) * 100;
      this.querySelector('[data-progress-value]').style.width = `${percent}%`;
      this.querySelector('[data-progress-text]').textContent = `Step ${this.currentStep + 1} of ${this.steps.length}`;
      this.querySelector('[data-error]').textContent = '';
      this.steps[this.currentStep].querySelector('input:checked, input')?.focus({ preventScroll: true });
    }

    resolveAreaRoutine(key) {
      const base = { key, ...this.routines[key] };
      const matches = (expected, actual) => {
        const wanted = this.normalizeKey(expected);
        return !wanted || wanted === '*' || wanted === 'any' || wanted === this.normalizeKey(actual);
      };
      const rule = this.rules.find((candidate) =>
        matches(candidate.area, key) &&
        matches(candidate.focus, this.answers.focus) &&
        matches(candidate.length, this.answers.length) &&
        matches(candidate.feel, this.answers.feel)
      );
      if (!rule) return base;
      const level = this.answers.length === 'complete' ? 'complete' : 'simple';
      return {
        ...base,
        ruleId: rule.id,
        ruleName: rule.name,
        title: rule.title || base.title,
        text: rule.text || base.text,
        notice: rule.notice || base.notice,
        collectionUrl: rule.collectionUrl || base.collectionUrl,
        [level]: rule.productKeys.length ? rule.productKeys : base[level]
      };
    }

    resolveRoutine() {
      const areaKeys = this.answers.areas?.length ? this.answers.areas : [this.answers.area || 'face'];
      const areaRoutines = areaKeys.map((key) => this.resolveAreaRoutine(key)).filter((routine) => routine?.title);
      if (areaRoutines.length <= 1) return { ...areaRoutines[0], areaKeys };

      const ruleNames = [...new Set(areaRoutines.map((routine) => routine.ruleName).filter(Boolean))];
      const ruleIds = [...new Set(areaRoutines.map((routine) => routine.ruleId).filter(Boolean))];
      return {
        key: areaKeys.join('+'),
        areaKeys,
        title: this.dataset.combinedTitle || 'Your combined routine',
        text: this.dataset.combinedText || 'A combined selection based on all the areas you chose.',
        notice: this.dataset.combinedNotice || 'This result offers cosmetic product guidance only. Check each product’s ingredients and directions before use.',
        collectionUrl: '',
        simple: this.uniqueProductKeys(areaRoutines.flatMap((routine) => routine.simple || [])),
        complete: this.uniqueProductKeys(areaRoutines.flatMap((routine) => routine.complete || [])),
        groups: areaRoutines.map((routine) => ({
          key: routine.key,
          label: routine.label || routine.title,
          simple: routine.simple || [],
          complete: routine.complete || []
        })),
        ruleName: ruleNames.join(' + '),
        ruleId: ruleIds.join(',')
      };
    }

    showResults() {
      this.activeLevel = this.answers.length === 'complete' ? 'complete' : 'simple';
      this.currentRoutine = this.resolveRoutine();
      this.form.hidden = true;
      this.results.hidden = false;
      this.updateResultHeader();
      this.setLevel(this.activeLevel);
      this.dispatchEvent(new CustomEvent('routine_finder:complete', { bubbles: true, detail: { category: this.currentRoutine.key, level: this.activeLevel, rule: this.currentRoutine.ruleId || null } }));
      this.results.focus({ preventScroll: true });
    }

    updateResultHeader() {
      this.querySelector('[data-result-title]').textContent = this.currentRoutine.title;
      this.querySelector('[data-result-text]').textContent = this.currentRoutine.text;
      this.querySelector('[data-result-notice]').textContent = this.currentRoutine.notice;
      if (this.answers.isSensitive || this.answers.feel === 'sensitive-feeling') {
        this.querySelector('[data-result-notice]').textContent += ' Because you selected sensitive-feeling, review the full ingredient list, consider patch testing, and seek professional advice if you have a diagnosed condition.';
      }
      const collectionLink = this.querySelector('[data-result-collection]');
      if (this.currentRoutine.collectionUrl) {
        collectionLink.href = this.currentRoutine.collectionUrl;
        collectionLink.hidden = false;
      } else {
        collectionLink.hidden = true;
      }
    }

    setLevel(level) {
      this.activeLevel = level;
      this.answers.length = level;
      this.currentRoutine = this.resolveRoutine();
      this.updateResultHeader();
      this.querySelector('[data-cart-status]').textContent = '';
      this.querySelectorAll('[data-level]').forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.level === level)));
      const keys = this.currentRoutine[level];
      const products = keys.map((key) => ({ ...this.catalog[key], key })).filter((product) => product.id);
      const list = this.querySelector('[data-products]');
      const isCombined = (this.currentRoutine.areaKeys?.length || 0) > 1;
      list.classList.toggle('routine-finder__products--grouped', isCombined);
      if (!products.length) {
        list.innerHTML = '<p class="routine-finder__configuration-error"><strong>This routine has no products configured.</strong><br>Open this section in the Theme Editor and select its products. Product metafields are not required.</p>';
      } else if (isCombined) {
        const usedProductIds = new Set();
        list.innerHTML = this.currentRoutine.groups.map((group) => {
          const groupProducts = (group[level] || [])
            .map((key) => ({ ...this.catalog[key], key }))
            .filter((product) => {
              if (!product.id || usedProductIds.has(String(product.id))) return false;
              usedProductIds.add(String(product.id));
              return true;
            });
          if (!groupProducts.length) return '';
          return `
            <section class="routine-finder__product-group" aria-labelledby="routine-group-${this.normalizeKey(group.key)}">
              <h4 class="routine-finder__group-heading" id="routine-group-${this.normalizeKey(group.key)}">${this.escape(group.label)}</h4>
              <div class="routine-finder__group-products">
                ${groupProducts.map((product) => this.renderProductCard(product)).join('')}
              </div>
            </section>`;
        }).join('');
      } else {
        list.innerHTML = products.map((product, index) => this.renderProductCard(product, `Step ${index + 1}`)).join('');
      }
      this.updateSelection();
      this.updateEmailTags();
    }

    renderProductCard(product, sequenceLabel = '') {
      return `
        <article class="routine-finder__product">
          ${product.image ? `<a class="routine-finder__product-media" href="${product.url}"><img src="${product.image}" alt="" width="480" height="480" loading="lazy"></a>` : ''}
          <div class="routine-finder__product-body">
            ${sequenceLabel ? `<p class="routine-finder__product-step">${sequenceLabel}</p>` : ''}
            <h4><a href="${product.url}">${this.escape(product.title)}</a></h4>
            <p class="routine-finder__product-price">${product.price}</p>
            ${product.routineNote ? `<p>${this.escape(product.routineNote)}</p>` : ''}
            <label class="routine-finder__include">
              <input type="checkbox" data-product-toggle value="${product.key}" ${product.available ? 'checked' : 'disabled'}>
              <span>${product.available ? 'Include in my routine' : 'Currently unavailable'}</span>
            </label>
          </div>
        </article>`;
    }

    updateSelection() {
      const toggles = [...this.querySelectorAll('[data-product-toggle]')];
      toggles.forEach((input) => input.closest('.routine-finder__product')?.classList.toggle('routine-finder__product--excluded', !input.checked));
      const selectedKeys = toggles.filter((input) => input.checked).map((input) => input.value);
      const products = selectedKeys.map((key) => this.catalog[key]).filter((product) => product?.id && product.available);
      const total = products.reduce((sum, product) => sum + Number(product.priceRaw || 0), 0);
      this.querySelector('[data-total]').textContent = this.formatMoney(total);
      const add = this.querySelector('[data-add-routine]');
      add.dataset.keys = selectedKeys.join(',');
      add.disabled = products.length === 0;
      add.textContent = products.length === 0 ? 'Select at least one product' : `Add ${products.length} ${products.length === 1 ? 'product' : 'products'} to basket`;
      this.updateEmailTags(selectedKeys);
    }

    updateEmailTags(selectedKeys = [...this.querySelectorAll('[data-product-toggle]:checked')].map((input) => input.value)) {
      const tags = this.querySelector('[data-interest-tags]');
      if (!tags || !this.currentRoutine) return;
      tags.value = [
        'newsletter',
        'quiz-completed',
        ...(this.currentRoutine.areaKeys || [this.currentRoutine.key]).map((key) => `interest-${this.normalizeKey(key)}`),
        `routine-${this.activeLevel}`,
        ...(this.currentRoutine.ruleName ? [`routine-rule-${this.normalizeKey(this.currentRoutine.ruleName)}`] : []),
        ...selectedKeys.map((key) => `routine-product-${key}`)
      ].join(',');
    }

    async addRoutine(button) {
      const products = button.dataset.keys.split(',').map((key) => this.catalog[key]).filter((product) => product?.id);
      if (!products.length) return;
      button.disabled = true;
      button.textContent = 'Adding…';
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: products.map((product) => ({
              id: product.id,
              quantity: 1,
              properties: {
                '_Routine recommendation': `${this.currentRoutine.key}-${this.activeLevel}`,
                ...(this.currentRoutine.ruleName ? { '_Routine rule': this.currentRoutine.ruleName } : {})
              }
            })),
            sections,
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'The routine could not be added.');
        const drawer = document.querySelector('cart-drawer');
        if (drawer && data.sections) drawer.renderContents({ ...data, id: products[0].id });
        else window.location.href = `${window.Shopify.routes.root}cart`;
        this.dispatchEvent(new CustomEvent('routine_finder:add', { bubbles: true, detail: { category: this.currentRoutine.key, level: this.activeLevel } }));
      } catch (error) {
        this.querySelector('[data-cart-status]').textContent = error.message;
        button.disabled = false;
        button.textContent = `Add ${this.activeLevel} routine to basket`;
      }
    }

    async submitEmail(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const button = form.querySelector('button[type="submit"]');
      const status = form.querySelector('[data-email-status]');
      if (!form.reportValidity()) return;
      button.disabled = true;
      button.dataset.originalText ||= button.textContent;
      button.textContent = 'Saving…';
      status.textContent = '';
      status.classList.remove('routine-finder__email-status--error', 'routine-finder__email-status--success');

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
        });
        const html = await response.text();
        const returnedPage = new DOMParser().parseFromString(html, 'text/html');
        const returnedForm = returnedPage.getElementById(form.id);
        const saved = response.url.includes('customer_posted=true') || Boolean(returnedForm?.querySelector('[data-email-posted-successfully]'));
        if (!response.ok || !saved) throw new Error('We could not save your email. Please check the address and try again.');

        status.textContent = status.dataset.successMessage || 'Your routine preferences have been saved.';
        status.classList.add('routine-finder__email-status--success');
        form.querySelector('input[type="email"]').disabled = true;
        button.textContent = 'Saved';
        this.dispatchEvent(new CustomEvent('routine_finder:email_saved', {
          bubbles: true,
          detail: { category: this.currentRoutine.key, level: this.activeLevel }
        }));
      } catch (error) {
        status.textContent = error.message;
        status.classList.add('routine-finder__email-status--error');
        button.disabled = false;
        button.textContent = button.dataset.originalText;
      }
    }

    restart() {
      this.form.reset();
      this.answers = {};
      this.results.hidden = true;
      this.showStep(0);
    }

    formatMoney(cents) {
      if (window.Shopify?.currency?.active) return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: window.Shopify.currency.active }).format(cents / 100);
      return `£${(cents / 100).toFixed(2)}`;
    }

    normalizeKey(value = '') {
      if (value === '*') return '*';
      return String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    uniqueProductKeys(keys = []) {
      const productIds = new Set();
      return keys.filter((key) => {
        const productId = this.catalog[key]?.id;
        if (!productId || productIds.has(String(productId))) return false;
        productIds.add(String(productId));
        return true;
      });
    }

    escape(value = '') {
      const div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }
  });
}
